import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { UserStat, impersonateUser, fetchUserSpendBreakdown, UserSpendBreakdown } from '../../../../services/adminService';
import PlanChangeModal from '../PlanChangeModal/PlanChangeModal';

interface UserDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: UserStat;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({ isOpen, onClose, user }) => {
    const [isPlanChangeModalOpen, setIsPlanChangeModalOpen] = useState(false);
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [spend, setSpend] = useState<UserSpendBreakdown | null>(null);
    const [isLoadingSpend, setIsLoadingSpend] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setIsLoadingSpend(true);
        fetchUserSpendBreakdown(user.id)
            .then((data) => { if (!cancelled) setSpend(data); })
            .catch((error) => {
                console.error('Error fetching user spend breakdown:', error);
                if (!cancelled) toast.error('Erro ao carregar detalhamento de custo do usuário.');
            })
            .finally(() => { if (!cancelled) setIsLoadingSpend(false); });
        return () => { cancelled = true; };
    }, [isOpen, user.id]);

    const handleImpersonate = async () => {
        if (isImpersonating) return;

        const confirmAccess = window.confirm(`Deseja entrar na conta de ${user.full_name || user.email}?\n\nVocê navegará na aplicação exatamente como este usuário e poderá visualizar e alterar os dados dele.`);
        if (!confirmAccess) return;

        setIsImpersonating(true);
        const toastId = toast.loading(`Acessando a conta de ${user.full_name || user.email}...`);

        try {
            const result = await impersonateUser(user.id, user.full_name || user.email || undefined);
            if (result.success) {
                toast.success('Acesso concedido! Redirecionando para visão do usuário...', { id: toastId });
                window.location.href = '/';
            } else {
                toast.error(result.error || 'Falha ao acessar conta.', { id: toastId });
                setIsImpersonating(false);
            }
        } catch {
            toast.error('Erro ao alternar para conta do usuário.', { id: toastId });
            setIsImpersonating(false);
        }
    };

    const formatDate = (date: string | null) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop - mais escuro e opaco */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 bg-black/70 dark:bg-black/80 backdrop-blur-md z-50"
            />

            {/* Modal */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose();
                }}
            >
                <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border-2 border-gray-200 dark:border-gray-700 shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-primary/30 dark:bg-primary/40 flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary text-2xl">person</span>
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-text-dark dark:text-white">
                                    {user.full_name || 'Usuário sem nome'}
                                </h2>
                                <p className="text-sm text-text-dark/60 dark:text-gray-400 mt-1">
                                    {user.email || 'Email não disponível'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-text-dark/70 dark:text-gray-400 hover:text-text-dark dark:hover:text-white transition-colors"
                        >
                            <span className="material-symbols-outlined text-3xl">close</span>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        {/* Acesso Administrativo à Conta do Usuário */}
                        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/20 text-amber-600 dark:text-amber-400 flex-shrink-0">
                                    <span className="material-symbols-outlined text-2xl">switch_account</span>
                                </span>
                                <div>
                                    <h4 className="font-bold text-sm text-text-dark dark:text-white">Acessar Visão do Usuário</h4>
                                    <p className="text-xs text-text-dark/60 dark:text-gray-400">
                                        Navegue na aplicação exatamente como este usuário para verificar leads, WhatsApp e realizar alterações.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleImpersonate}
                                disabled={isImpersonating}
                                className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                                {isImpersonating ? (
                                    <>
                                        <span className="material-symbols-outlined text-base animate-spin">sync</span>
                                        <span>Acessando...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-base">login</span>
                                        <span>Entrar na Conta</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Plano */}
                            <div className="bg-background-light dark:bg-background-dark rounded-lg p-4">
                                <p className="text-sm text-text-dark/70 dark:text-text-light/70 mb-2">Plano</p>
                                <div className="flex items-center justify-between">
                                    <span className={`px-4 py-2 rounded-lg text-sm font-semibold ${user.subscription_status === 'pro' ? 'bg-green-500/20 text-green-700 dark:text-green-400' :
                                        user.subscription_status === 'basic' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-400' :
                                            user.subscription_status === 'free' ? 'bg-gray-500/20 text-gray-700 dark:text-gray-400' :
                                                'bg-red-500/20 text-red-700 dark:text-red-400'
                                        }`}>
                                        {user.subscription_status.toUpperCase()}
                                    </span>
                                    <button
                                        onClick={() => setIsPlanChangeModalOpen(true)}
                                        className="px-3 py-1 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-colors"
                                    >
                                        Alterar
                                    </button>
                                </div>
                            </div>

                            {/* País */}
                            <div className="bg-background-light dark:bg-background-dark rounded-lg p-4">
                                <p className="text-sm text-text-dark/70 dark:text-gray-400 mb-2">País</p>
                                <p className="text-lg font-semibold text-text-dark dark:text-white">
                                    {user.country || 'Não especificado'}
                                </p>
                            </div>

                            {/* Cadastro */}
                            <div className="bg-background-light dark:bg-background-dark rounded-lg p-4">
                                <p className="text-sm text-text-dark/70 dark:text-gray-400 mb-2">Cadastrado em</p>
                                <p className="text-sm font-medium text-text-dark dark:text-white">
                                    {formatDate(user.user_since)}
                                </p>
                            </div>

                            {/* Última Atividade */}
                            <div className="bg-background-light dark:bg-background-dark rounded-lg p-4">
                                <p className="text-sm text-text-dark/70 dark:text-gray-400 mb-2">Última Atividade</p>
                                <p className="text-sm font-medium text-text-dark dark:text-white">
                                    {formatDate(user.last_activity)}
                                </p>
                            </div>
                        </div>

                        {/* Stats Cards */}
                        <div className="grid grid-cols-3 gap-4">
                            {/* Leads */}
                            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-lg p-4 border border-purple-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="material-symbols-outlined text-purple-500">contact_page</span>
                                    <p className="text-sm text-text-dark/70 dark:text-gray-400">Leads Salvos</p>
                                </div>
                                <p className="text-2xl font-bold text-text-dark dark:text-white">
                                    {user.total_leads}
                                </p>
                            </div>

                            {/* Prospectados */}
                            <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-lg p-4 border border-green-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="material-symbols-outlined text-green-500">check_circle</span>
                                    <p className="text-sm text-text-dark/70 dark:text-gray-400">Prospectados</p>
                                </div>
                                <p className="text-2xl font-bold text-text-dark dark:text-white">
                                    {user.prospected_leads}
                                </p>
                            </div>

                            {/* Pesquisas */}
                            <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/10 rounded-lg p-4 border border-orange-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="material-symbols-outlined text-orange-500">search</span>
                                    <p className="text-sm text-text-dark/70 dark:text-gray-400">Pesquisas</p>
                                </div>
                                <p className="text-2xl font-bold text-text-dark dark:text-white">
                                    {user.total_searches}
                                </p>
                            </div>
                        </div>

                        {/* WhatsApp Info */}
                        <div className="bg-background-light dark:bg-background-dark rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="material-symbols-outlined text-green-500">chat</span>
                                <h3 className="font-semibold text-text-dark dark:text-white">WhatsApp</h3>
                            </div>
                            {user.whatsapp_instance ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-text-dark/70 dark:text-gray-400">Instância:</span>
                                        <span className="text-sm font-medium text-text-dark dark:text-white">
                                            {user.whatsapp_instance}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-text-dark/70 dark:text-gray-400">Status:</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2 h-2 rounded-full ${user.whatsapp_connected ? 'bg-green-500' : 'bg-red-500'}`} />
                                            <span className="text-sm font-medium text-text-dark dark:text-white">
                                                {user.whatsapp_connected ? 'Conectado' : 'Desconectado'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-text-dark/50 dark:text-text-light/50">
                                    WhatsApp não configurado
                                </p>
                            )}
                        </div>

                        {/* Custo e Gastos */}
                        <div className="bg-background-light dark:bg-background-dark rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="material-symbols-outlined text-pink-500">payments</span>
                                <h3 className="font-semibold text-text-dark dark:text-white">Custo e Gastos com IA</h3>
                            </div>
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-text-dark/60 dark:text-gray-400 mb-1">Total de Tokens</p>
                                    <p className="text-lg font-bold text-text-dark dark:text-white">
                                        {user.total_tokens_used.toLocaleString()}
                                    </p>
                                </div>
                                <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-text-dark/60 dark:text-gray-400 mb-1">Custo Total (histórico)</p>
                                    <p className="text-lg font-bold text-text-dark dark:text-white">
                                        ${user.total_cost.toFixed(4)}
                                    </p>
                                </div>
                                <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-text-dark/60 dark:text-gray-400 mb-1">Últimos 30 dias</p>
                                    <p className="text-lg font-bold text-text-dark dark:text-white">
                                        {isLoadingSpend ? '...' : `$${(spend?.costLast30Days ?? 0).toFixed(4)}`}
                                    </p>
                                </div>
                            </div>

                            {isLoadingSpend ? (
                                <div className="flex items-center justify-center py-6 text-text-dark/50 dark:text-gray-400 text-sm gap-2">
                                    <span className="material-symbols-outlined animate-spin text-lg">sync</span>
                                    Carregando detalhamento...
                                </div>
                            ) : (
                                <>
                                    {/* Disparos em massa recentes */}
                                    <div className="mb-4">
                                        <p className="text-xs font-semibold text-text-dark/70 dark:text-gray-400 uppercase mb-2">
                                            Disparos em Massa Recentes
                                        </p>
                                        {spend && spend.recentBulkSends.length > 0 ? (
                                            <div className="space-y-1.5">
                                                {spend.recentBulkSends.map((b) => (
                                                    <div key={b.id} className="flex items-center justify-between text-xs bg-white dark:bg-gray-900 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                                                        <span className="text-text-dark/70 dark:text-gray-400">
                                                            {format(new Date(b.created_at), 'dd/MM/yyyy HH:mm')}
                                                        </span>
                                                        <span className="text-text-dark dark:text-white">
                                                            {b.total_recipients} destinatários
                                                        </span>
                                                        <span className="text-green-600 dark:text-green-400">{b.messages_sent} enviadas</span>
                                                        <span className="text-red-600 dark:text-red-400">{b.messages_failed} falhas</span>
                                                        <span className={`px-2 py-0.5 rounded-full font-medium ${b.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                                                            b.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                                                                'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                                            }`}>
                                                            {b.status}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-text-dark/50 dark:text-gray-500">Nenhum disparo em massa registrado.</p>
                                        )}
                                    </div>

                                    {/* Uso de IA recente */}
                                    <div>
                                        <p className="text-xs font-semibold text-text-dark/70 dark:text-gray-400 uppercase mb-2">
                                            Uso de IA Recente
                                        </p>
                                        {spend && spend.recentUsage.length > 0 ? (
                                            <div className="space-y-1.5">
                                                {spend.recentUsage.map((u) => (
                                                    <div key={u.id} className="flex items-center justify-between text-xs bg-white dark:bg-gray-900 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                                                        <span className="text-text-dark/70 dark:text-gray-400">
                                                            {format(new Date(u.created_at), 'dd/MM/yyyy HH:mm')}
                                                        </span>
                                                        <span className="text-text-dark dark:text-white">{u.model_name}</span>
                                                        <span className="text-text-dark/70 dark:text-gray-400">
                                                            {(u.input_tokens + u.output_tokens).toLocaleString()} tokens
                                                        </span>
                                                        <span className="text-orange-600 dark:text-orange-400 font-medium">
                                                            ${u.cost_usd.toFixed(6)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-text-dark/50 dark:text-gray-500">Nenhum uso de IA registrado.</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Plan Change Modal */}
            <PlanChangeModal
                isOpen={isPlanChangeModalOpen}
                onClose={() => setIsPlanChangeModalOpen(false)}
                user={user}
                onSuccess={() => {
                    setIsPlanChangeModalOpen(false);
                    onClose(); // Fechar modal principal para forçar refresh
                }}
            />
        </>
    );
};

export default UserDetailModal;
